/** /zh/product-requirements-document — Chinese version of the PRD guide
 *  landing page. Native zh content, same structure as
 *  ProductRequirementsDocument.tsx. Targets the "PRD 是什么 / PRD 文档 /
 *  产品需求文档" cluster while doubling as product differentiation. */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

export function ProductRequirementsDocumentZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/product-requirements-document"
      documentTitle="PRD（产品需求文档）是什么——以及一直成立的那一种 · Draftlize"
      documentDescription="PRD 即产品需求文档（Product Requirements Document）：讲清问题、目标、非目标、用户故事、需求、成功指标、依赖和待解问题。可问题在于——PRD 写完那一刻就开始漂移。Draftlize 让 PRD 里每个决策都成为可寻址卡片，一个决策变了，依赖它的全部自动标记 stale。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · PRD</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            PRD 到底是什么——<br/>
            <em>以及一直成立的那一种。</em>
          </h1>
          <p className="lede">
            <em>产品需求文档</em>——PRD——是那一份说清「你在做什么、做给谁、怎么算成功」的文档。写好了，它能把一个模糊的想法变成团队和工程师不用开会就能动手的东西。这一页做两件事：先把 PRD 是什么、一份好的 PRD 包含哪些部分讲清楚；再诚实地讲那个没人提的部分——一份写在 Google Doc 或 Notion 里的 PRD，从你发布那一刻起就开始漂移，因为它里面没有任何东西，把一个决策连到依赖它的那些需求上。Draftlize 从根上修这个问题。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">那么，<em>PRD 是什么？</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            产品需求文档是一个功能或产品的真相源：它讲清你要解决的问题、你想要的结果、以及这个东西必须具备的具体行为。它存在的意义，是让动手做它的人不必从一条 Slack 消息里反推意图——<em>PRD 的本质</em>，剥掉仪式感，就是<em>「对『我们在做什么、为什么做』这个问题，已经达成一致的那个答案」</em>。
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            它不是讲「怎么做」的设计文档——那是工程设计文档的事。PRD 在它的上游：先定义清楚<em>做什么</em>和<em>为什么</em>，让<em>怎么做</em>有东西可服务。一份好的 PRD 短到能一口气读完，又决断到让两个读者读完后脑子里是同一幅画。篇幅不是优点；一份把取舍摊开讲明白的单页，胜过十页处处含糊。
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: 0 }}>
            格式并不神圣。亚马逊写六页叙事加一份新闻稿；一家创业公司可能在 wiki 里写半页。重要的不是<em>PRD 文档</em>长什么样——而是下面这些部分有没有被诚实地回答。下面就是这些部分。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">解剖</span>
            <h2 className="serif">一份好的 PRD <em>包含什么。</em></h2>
          </div>
          <p>八个部分。不需要的名字可以略过，但底下那个问题要答。无论你写在文档里、从一份 <a href="/zh/prd-template" style={{ color: 'var(--amber)' }}>PRD 模板</a>复制、还是在 Draftlize 里搭，骨架都是这一套——下一节讲的是：当每个部分不再是一段文字、而变成一张卡片时，会有什么不同。</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: 'Problem · 问题',
              hint: '为什么要做',
              body: '用户的痛或业务的缺口，在任何方案之前先讲清。如果你不提自己那个功能就描述不出问题，那你是在为一个还没找到的方案找问题。这是为整份文档正名的那一部分。',
            },
            {
              k: 'Goals · 目标',
              hint: '什么叫成功',
              body: '这个产品必须达成什么，说人话。三个以内。目标是你之后砍范围时用的那把尺——任何不服务于某个目标的东西，都是该砍的候选。',
            },
            {
              k: 'Non-goals · 非目标',
              hint: '这次不做什么',
              body: '那些诱人、但你这一轮刻意不做的东西。任何 PRD 里最被低估的一节，也是最能挡住「拖垮一次发版的范围蔓延」的一节。写下一个非目标，是一个决策，不是一个遗漏。',
            },
            {
              k: 'User stories · 用户故事',
              hint: '谁，在做什么',
              body: '用用户的话写的具体场景——「作为 PM，我想从一份 spec 里引用某个决策，这样就不用再解释一遍。」它们把每条需求都拴回「一个人在做一件事」，让需求保持诚实。',
            },
            {
              k: 'Requirements · 需求',
              hint: '具体行为',
              body: '产品到底必须做到什么，具体到能照着做、照着测。PRD 的主体。每条需求都该能追溯到一个目标和一个用户故事——两个都搭不上的需求，是装饰。',
            },
            {
              k: 'Success metrics · 成功指标',
              hint: '怎么知道成了',
              body: '那些告诉你「成了」的数字——激活、留存、任务完成率，看目标暗示了什么。提前定，趁结果还没法影响你的选择。没有它们,「成没成?」就从一次读数，变成一场争论。',
            },
            {
              k: 'Dependencies · 依赖',
              hint: '它建立在什么之上',
              body: '这份 PRD 所假设的那些决策、系统和其他工作。定价、一个必须存在的 API、另一份文档里做的某个决策。在静态文档里这是一张你会忘记回看的清单——而它正是决定「PRD 会不会随着事情变动而仍然成立」的那一部分。',
            },
            {
              k: 'Open questions · 待解问题',
              hint: '还没定的',
              body: '那些你还不知道的事，诚实地写下来，而不是糊过去。一份假装一切都已敲定的 PRD 是在说谎；一份列出自己未知项的 PRD，恰恰告诉读者风险藏在哪。',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          这就是解剖。下面：为什么一份 PRD 从发布那天起就开始漂移——以及我们怎么应对。
        </p>
      </section>

      <section className="compare" style={{ paddingTop: 8 }}>
        <div className="head">
          <div>
            <span className="label">范例</span>
            <h2 className="serif">一份简短的 <em>PRD 范例。</em></h2>
          </div>
          <p>同样这八个部分，为一个小功能填好——报表页的 CSV 导出。刻意写短：好的 PRD 该有多长取决于决策需要多少，而不是越长越好。</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: '问题', body: '需要把报表数据拿到别处用的用户，只能一行行手动复制进电子表格——慢、易错、还是客服反复收到的抱怨。数据根本没有导出口。' },
            { k: '目标', body: '1）任何用户都能一键把当前报表导出成 CSV。2）导出的文件和屏幕上看到的一致——筛选、排序、可见列。3）一万行的导出在五秒内完成。' },
            { k: '非目标', body: '定时或周期导出、XLSX 和 PDF 格式、以及导出当前筛选视图之外的数据——都有意推到后续版本。' },
            { k: '用户故事', body: '作为分析师，我希望把筛选后的报表导出成 CSV，好在自己的电子表格里做透视，而不用重新录入数字。' },
            { k: '需求', body: '报表工具栏上一个导出按钮；导出当前筛选、排序后的视图和可见列；CSV 为 UTF-8 且带表头行；完成时用 toast 提示；超过 5 万行的导出被拦下并给出明确提示。' },
            { k: '成功指标', body: '60 天内手动复制粘贴的客服工单减半；上线一个月内，每周查看报表的活跃用户里至少 20% 用过导出。' },
            { k: '依赖', body: '报表 API 必须提供一个带筛选的导出端点（平台团队负责）。依赖「导出在所有付费套餐都包含」这个定价决策。' },
            { k: '待解问题', body: '要不要包含被软删除的行？超大导出是否改成异步、完成后邮件通知的流程？' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '132px 1fr', gap: 24, padding: '18px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <span className="serif" style={{ fontSize: 17, fontWeight: 500, color: 'var(--ink-0)', lineHeight: 1.2 }}>{f.k}</span>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.65, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p style={{ maxWidth: 720, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)', marginTop: 20 }}>
          写在文档里，这个范例今天准、明天就在漂：改动「依赖」里的定价决策，上面那些需求没有任何东西会被告知——它们现在建立在一个已经变了的决定上。在 Draftlize 里每一节都是一张连着它所依赖决策的卡片，上游一变就把相关部分标成 stale，而不是让范例悄悄变错。
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">为什么每份 PRD 都会<em>漂移。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">它在发布那天最准，往后再没那么准</h3>
            <p>PRD 是某一刻所做决策的一张快照。然后产品往前走——价格改了、一个目标被砍了、API 上线时跟假设的不一样。文档不会跟着走。等到有人回头读它，已经有相当一部分悄悄错了，而你分不清错的是哪一部分，于是整份文档都不再被信任。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">没有东西把一个决策连到依赖它的需求</h3>
            <p>你在「目标」一节里定了定价。下游三条需求都假设了这个价。决策一改，那三条需求现在全错了——可一份 Google Doc 根本不知道它们是连着的，于是没人更新，直到一次构建出错、或一次演示让你尴尬。「依赖」一节本该接住这件事；但它是文字，所以接不住。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">保持它最新，是个注定输的杂活</h3>
            <p>维护 PRD 是个跟「发版」抢时间的活，而发版永远赢。于是文档变成「第一天你怎么想」的记录，而不是「现在什么是真的」——团队于是不再打开它，这就把它存在的全部理由给废了。<a href="/zh/decision-log" style={{ color: 'var(--amber)' }}>决策日志</a>因同样的原因落得同样下场:靠手动，自律赢不了熵。</p>
          </div>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">一份 PRD 文档 vs <em>一份活的 PRD。</em></h2>
          </div>
          <p>写在 Notion 或文档里的 PRD，是一份静态记录。解法不是更好的模板、也不是更强的自律——而是一个知道「某个决策变了」并且会去告诉「一切依赖它的东西」的 substrate。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Google Doc / Notion / Confluence</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>PRD 的每个部分</td><td className="ours yes">一张结构化、可寻址的卡片</td><td className="partial">一个标题加一段文字</td></tr>
            <tr><td>之后还找得到</td><td className="ours yes">能从任意 spec 按 URL / ID 引用</td><td className="no">埋在一份你得 grep 的文档里</td></tr>
            <tr><td>决策变了，下游知道吗</td><td className="ours yes">自动标记 stale</td><td className="no">没有连接，悄悄腐烂</td></tr>
            <tr><td>AI 用得上吗</td><td className="ours yes">经 MCP 原生读 + 写</td><td className="partial">顶多读个纯文本</td></tr>
            <tr><td>谁来保持一致</td><td className="ours yes">substrate 自动追踪</td><td className="no">全靠你的意志力</td></tr>
            <tr><td>两条需求互相冲突</td><td className="ours yes">自动浮现</td><td className="no">下次复盘才发现</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">同一份 PRD，<em>变成活的。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">每个决策都是一张可寻址卡片</h3>
            <p>在 Draftlize 里，PRD 不是一份长文档——每个部分都是一张结构化卡片:一个问题、一个目标、一个非目标、一条需求、一个成功指标。结构化、有稳定 ID、能从 spec 或 thread 引用，而不是埋在一个 wiki 页里。「依据决策 c-04」成了真实的引用，而不是模糊的记忆。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">改一个决策，依赖自动标 stale</h3>
            <p>因为依赖是真实的链接、而不是清单里的几个名词，改掉一个上游决策，每张依赖它的卡片自动变 stale——就像构建系统让一个改动文件的所有下游失效一样。「依赖」一节许下的承诺，substrate 替它兑现。你一改，就当场看见自己改坏了什么。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI agent 能读也能写这份 PRD</h3>
            <p>Claude Code 或 Cursor，经 MCP，在起草下一条需求前读完每个相关决策，再把新决策按同样的形状写回去。PRD 不再是一份写给「永不回来的读者」的文档，而成了 agent 每一轮都来查的上下文——于是它记下的，是现在真正成立的东西，而不是上线那天成立的东西。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          PRD 不是因为写得差才失败。它失败，是因为没有东西在「它的某个决策已经不成立」时告诉它一声。
        </blockquote>
        <cite>八个部分留着。让 substrate 替你把它们保持最新。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '一句话讲，PRD 是什么？', a: '产品需求文档（PRD）就是对「我们要做什么、为谁做、怎么算做成了」这个问题的一致答案——它写清问题、目标、非目标、用户故事、需求、成功指标、依赖和待解问题，好让团队不用再从一堆 Slack 消息里拼凑意图就能开工。' },
            { q: 'PRD 该由谁来写？', a: '产品经理负责它，但一份好的 PRD 是和工程、设计以及掌握业务背景的人一起写的。PM 的责任是让里面的决策清晰、且始终是最新的——而不是一个人闷头把它敲完。' },
            { q: 'PRD 和技术设计文档有什么区别？', a: 'PRD 定义做什么和为什么，在上游；技术（工程设计）文档定义怎么做，在下游。PRD 说「用户要能从一份 spec 里引用某个决策」，设计文档说这个引用怎么存、怎么渲染。一份 PRD 往往会派生出好几份设计文档。' },
            { q: 'PRD 和 BRD 有什么区别？', a: 'BRD（业务需求文档）记录业务诉求和成功标准——公司为什么该投入。PRD 把它翻译成产品到底要做到什么。BRD 论证这笔赌注，PRD 让它可被构建。很多团队把两者合成一份。' },
            { q: 'Draftlize 里的 PRD 和 Notion / Google Doc 里的有什么不同？', a: '在文档里，PRD 的每一部分都是散文，而且没有任何东西把一个决策连到依赖它的需求——所以决策一变，它就悄悄开始漂移。在 Draftlize 里每一部分是一张有类型、可寻址的卡片；上游决策一改，每张依赖它的卡片自动标记为过期，AI agent 还能经 MCP 直接读写 PRD，而不是去解析一堆纯文本。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">用 $5 <em>免费写下你的第一份活 PRD。</em></h2>
        </div>
        <div className="trust-strip">
          <span>注册送 $5</span>
          <span>按量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            建一个项目，用你已有的文档把它喂起来，让 agent 把 PRD 起草成结构化卡片——再替你把每个决策、以及每条依赖它的需求，保持最新。
          </p>
          <a className="btn-primary" href="/">用 $5 免费开始</a>
        </div>
      </section>
      <TemplateCta template="prd" locale="zh" />
    </MarketingShell>
  );
}
